<script setup lang="ts">
import type {
  FolderManagerItem as FolderManagerItemData,
  FolderManagerProps,
} from './folder-manager';
import FolderManagerItem from './FolderManagerItem.vue';
import IconButton from '../IconButton/IconButton.vue';
import DraggableList from '../DraggableList/DraggableList.vue';
import { useIsTouchDevice } from '../useIsTouchDevice';
import { computed, ref, watch } from 'vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';

const props = withDefaults(defineProps<FolderManagerProps>(), {
  hideAddFolder: false,
  hidePrintButton: true,
  hideTitle: false,
});

const emits = defineEmits<{
  selectItem: [id: string];
  addFolder: [];
  print: [];
  reorder: [ids: string[]];
  dropItem: [id: string];
}>();

const isTouchDevice = useIsTouchDevice();

// ── Sortable grouping ─────────────────────────────────────────────────────

/**
 * Disclaimer for how sorting is implemented:
 * There must only be one series of consecutive sortable folders
 * in between the static ones. If that is not the case anymore for some future
 * scenario, we would have to rethink the implementation.
 */

const firstSortableIndex = computed(() =>
  props.folders.findIndex((f) => f.sortable),
);

const lastSortableIndex = computed(() => {
  let last = -1;
  props.folders.forEach((f, i) => {
    if (f.sortable) last = i;
  });
  return last;
});

const staticFoldersBefore = computed<FolderManagerItemData[]>(() => {
  if (firstSortableIndex.value === -1) return props.folders;
  return props.folders.slice(0, firstSortableIndex.value);
});

const sortableFolders = computed<FolderManagerItemData[]>(() =>
  props.folders.filter((f) => f.sortable),
);

const staticFoldersAfter = computed<FolderManagerItemData[]>(() => {
  if (lastSortableIndex.value === -1) return [];
  return props.folders.slice(lastSortableIndex.value + 1);
});

const hasSortableFolders = computed(() => sortableFolders.value.length > 0);

// Local copy used by DraggableList so that drag state is independent of prop updates
const localSortableFolders = ref([...sortableFolders.value]);

watch(sortableFolders, (newFolders) => {
  localSortableFolders.value = [...newFolders];
});

const handleMoved = (): void => {
  emits(
    'reorder',
    localSortableFolders.value.map((f) => f.id),
  );
};
</script>

<template>
  <div class="flex flex-col rounded-2xl bg-slate-50 px-4 py-4 transition-all">
    <div v-if="!props.hideTitle" class="flex items-center pb-2 text-slate-500">
      <label
        class="flex flex-1 items-center gap-2 px-2 text-xs font-semibold uppercase"
        >{{ props.title }}

        <button v-if="!props.hidePrintButton" @click="emits('print')">
          <HugeIcon icon="print" :size="16" />
        </button>
      </label>

      <div v-if="$slots['title-actions']" class="flex shrink-0 items-center">
        <slot name="title-actions" />
      </div>

      <IconButton
        v-if="!props.hideAddFolder"
        variant="ghost-neutral"
        icon="plus"
        class="mr-2"
        @click="emits('addFolder')"
      />
    </div>

    <div v-if="$slots['below-title']" class="pb-2">
      <slot name="below-title" />
    </div>

    <!-- Static folders rendered before the sortable block -->
    <FolderManagerItem
      v-for="folder in staticFoldersBefore"
      :key="folder.id"
      :folder="folder"
      :selected="props.selectedFolderId === folder.id"
      :actions-placement="props.actionsPlacement"
      :is-touch-device="isTouchDevice.isTouch.value"
      @select="emits('selectItem', folder.id)"
      @drop="emits('dropItem', folder.id)"
    />

    <!-- Sortable block -->
    <DraggableList
      v-if="hasSortableFolders"
      v-model="localSortableFolders"
      item-key="id"
      group-name="folder-manager"
      @moved="handleMoved"
    >
      <template #item="{ item }">
        <FolderManagerItem
          :folder="item"
          :selected="props.selectedFolderId === item.id"
          :actions-placement="props.actionsPlacement"
          :is-touch-device="isTouchDevice.isTouch.value"
          @select="emits('selectItem', item.id)"
          @drop="emits('dropItem', item.id)"
        />
      </template>
    </DraggableList>

    <!-- Static folders rendered after the sortable block -->
    <FolderManagerItem
      v-for="folder in staticFoldersAfter"
      :key="folder.id"
      :folder="folder"
      :selected="props.selectedFolderId === folder.id"
      :actions-placement="props.actionsPlacement"
      :is-touch-device="isTouchDevice.isTouch.value"
      @select="emits('selectItem', folder.id)"
      @drop="emits('dropItem', folder.id)"
    />

    <div v-if="$slots['list-footer']" class="flex justify-center px-2 pt-2">
      <slot name="list-footer" />
    </div>
  </div>
</template>
